import {
    faFilter,
    faPlus,
    faTimes,
    faCheck,
    faMoneyBillWave,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import FilterOffCanvas, {
    FilterOption,
} from "components/common/FilterOffCanvas";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { ChangeEvent, useEffect, useState, useMemo } from "react";
import { Spinner, Card, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import FormaPagamentoTable, {
    formaPagamentoTableColumns,
} from "components/tables/FormaPagamentoTable";
import { formasPagamentoBreadcrumbItems, ActiveFilters, FilterState } from "data/formasPagamento";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";
import { useToast } from "providers/ToastProvider";
import { useFormasPagamento } from "hooks/useFormaPagamento";

const FormasPagamento = () => {
    const [searchTerm, setSearchTerm] = useState<string>("");
    const { showToast } = useToast();
    const [showFilterCanvas, setShowFilterCanvas] = useState<boolean>(false);

    // Add pagination state
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    // Initialize filter state
    const [filterState, setFilterState] = useState<FilterState>({
        active: { formaPagamentoStatus: "active" },
        pending: null,
        filtersPending: false,
    });

    // Configuração das opções de filtro
    const filterOptions: FilterOption[] = [
        {
            id: "formaPagamentoStatus",
            label: "Status da Forma de Pagamento",
            type: "radioCard",
            options: [
                {
                    value: "active",
                    label: "Ativas",
                    description: "Apenas formas de pagamento ativas",
                    icon: faCheck,
                },
                {
                    value: "deleted",
                    label: "Inativas",
                    description: "Apenas formas de pagamento inativas",
                    icon: faTrash,
                },
                {
                    value: "all",
                    label: "Todas",
                    description: "Ativas e inativas",
                    icon: faMoneyBillWave,
                },
            ],
        },
    ];

    const activeFilterCount = filterState.active.formaPagamentoStatus !== "active" ? 1 : 0;

    // Determinar se incluir inativas baseado no filtro
    const incluirInativas = filterState.active.formaPagamentoStatus === "all" || filterState.active.formaPagamentoStatus === "deleted";

    // Query para buscar dados usando o hook
    const dataQuery = useFormasPagamento(incluirInativas);

    // Filtrar dados localmente apenas por termo de busca (status já filtrado pelo backend)
    const filteredData = useMemo(() => {
        if (!dataQuery.data) return [];

        let filtered = dataQuery.data;

        // Filtrar por termo de busca
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item.descricao.toLowerCase().includes(searchLower) ||
                item.tipo.toLowerCase().includes(searchLower) ||
                (item.contaDestinoNome && item.contaDestinoNome.toLowerCase().includes(searchLower))
            );
        }

        return filtered;
    }, [dataQuery.data, searchTerm]);

    const table = useReactTable({
        data: filteredData || [],
        columns: formaPagamentoTableColumns,
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getCoreRowModel: getCoreRowModel(),
        manualPagination: false,
        onPaginationChange: setPagination,
        state: {
            pagination,
        },
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    // Reset pagination when search term or filters change
    useEffect(() => {
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, [searchTerm, filterState.active]);

    const handleApplyFilters = (filters: Record<string, any>) => {
        // Apply filters immediately instead of storing as pending
        setFilterState((prev) => ({
            ...prev,
            active: filters as ActiveFilters,
            pending: null,
            filtersPending: false,
        }));

        // Reset to first page when applying new filters
        setPagination(prev => ({ ...prev, pageIndex: 0 }));

        // Close the filter canvas
        setShowFilterCanvas(false);
    };

    const handleClearFilters = () => {
        setFilterState((prev) => ({
            ...prev,
            active: { formaPagamentoStatus: "active" },
            pending: null,
            filtersPending: false,
        }));
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
    };

    // Handle loading state
    if (dataQuery.isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
            </div>
        );
    }

    // Handle error state
    if (dataQuery.isError) {
        const error = dataQuery.error as any;

        // Check if it's a permission error (status 403)
        if (error?.response?.status === 403) {
            return (
                <div>
                    <PageBreadcrumb items={formasPagamentoBreadcrumbItems} />
                    <div className="position-relative" style={{ minHeight: "300px" }}>
                        <AccessDeniedOverlay>
                            <div className="text-center">
                                <h4 className="mb-3">Acesso Negado</h4>
                                <p className="mb-0">
                                    Você não possui permissão para visualizar as formas de pagamento.
                                </p>
                            </div>
                        </AccessDeniedOverlay>
                    </div>
                </div>
            );
        }

        return (
            <div className="text-center py-5 text-danger">
                {(dataQuery.error as Error).message}
            </div>
        );
    }

    return (
        <div>
            <PageBreadcrumb items={formasPagamentoBreadcrumbItems} />
            <div className="mb-9">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="mb-0">Formas de Pagamento</h2>
                    <Button
                        variant="primary"
                        as={Link}
                        to="/cadastros/formas-pagamento/novo"
                        className="px-4 py-2"
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Nova Forma de Pagamento
                    </Button>
                </div>

                <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
                    <div className="d-flex gap-2 flex-grow-1">
                        <SearchBox
                            placeholder="Pesquisar formas de pagamento por código, descrição ou tipo..."
                            value={searchTerm}
                            onChange={handleSearchInputChange}
                            className="flex-grow-1"
                        />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {activeFilterCount > 0 && (
                            <Badge
                                bg="light"
                                text="dark"
                                className="d-flex align-items-center py-2 px-3 border shadow-sm"
                                style={{ borderRadius: "50px" }}
                            >
                                <span>
                                    {filterState.active.formaPagamentoStatus === "deleted" &&
                                        "Mostrando inativas"}
                                    {filterState.active.formaPagamentoStatus === "all" && "Mostrando todas"}
                                </span>
                                <Button
                                    variant="link"
                                    className="p-0 ms-2 text-body-secondary border-0"
                                    onClick={handleClearFilters}
                                    style={{ fontSize: "14px" }}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                            </Badge>
                        )}

                        <Button
                            variant={activeFilterCount > 0 ? "primary" : "outline-primary"}
                            className="px-3 py-2 d-flex align-items-center"
                            onClick={() => setShowFilterCanvas(true)}
                        >
                            <FontAwesomeIcon icon={faFilter} className="me-2" />
                            Filtros
                            {activeFilterCount > 0 && (
                                <Badge
                                    bg="light"
                                    text="primary"
                                    className="ms-2 d-flex align-items-center justify-content-center"
                                    style={{ borderRadius: "50%", width: "20px", height: "20px" }}
                                >
                                    {activeFilterCount}
                                </Badge>
                            )}
                        </Button>
                    </div>
                </div>

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        <AdvanceTableProvider {...table}>
                            <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                                <FormaPagamentoTable />
                            </div>
                        </AdvanceTableProvider>
                    </Card.Body>
                </Card>
            </div>

            <FilterOffCanvas
                show={showFilterCanvas}
                onHide={() => setShowFilterCanvas(false)}
                onApplyFilters={handleApplyFilters}
                title="Filtros de Formas de Pagamento"
                filterOptions={filterOptions}
                activeFilters={filterState.active}
            />
        </div>
    );
};

export default FormasPagamento; 